import { ButtonBase, Modal } from '@mui/material';
import { TextLogo } from '@packages/shared-ui/components';
import { useId } from 'react';
import Button, { ButtonVariant } from '@/components/BaseLibrary/Button/Button';
import Icon, { IconName } from '@/components/Icon/Icon';
import ArtDirectedImage, {
  type ImageSources,
} from '@/components/Image/ArtDirectedImage';
import RichText from '@/components/RichText/RichText';
import {
  type FrontifyImageContent,
  isInternalLink,
} from '@/contentful/contentful-exports';
import { brandColors } from '@/styles/theme/values/brandColors';
import type { ActionModalCF } from '@/types';
import Heading from '../Heading/Heading';
import useStyles from './ActionModal.styles';

const getImages = (
  images: FrontifyImageContent[] | null,
  isHorizontal: boolean
): ImageSources | null => {
  if (!images || images.length === 0) return null;

  if (images.length === 2 && isHorizontal) {
    return {
      md: [images[1], 600],
      xs: [images[0], 300],
      fallback: [images[0], 300],
    };
  }

  return {
    md: [images[0], 600],
    xs: [images[0], 300],
    fallback: [images[0], 300],
  };
};

interface ActionModalProps {
  open: boolean;
  onClose: () => void;
  modalContent: ActionModalCF;
}

export enum ModalOrientation {
  HORIZONTAL = 'Horizontal',
  VERTICAL = 'Vertical',
}

export enum HeadingSize {
  SM = 'SM',
  LG = 'LG',
}

export default function ActionModal({
  open,
  onClose,
  modalContent,
}: ActionModalProps) {
  const {
    showLogo,
    orientation,
    images,
    heading,
    headingSize,
    subheading,
    termsAndConditions,
    cta,
    secondaryCta,
    secondaryCtaLabel,
    closeButtonStyle,
  } = modalContent.fields;
  const isHorizontal = orientation === ModalOrientation.HORIZONTAL;

  const titleId = useId();
  const descriptionId = useId();

  const { classes, cx } = useStyles({ isHorizontal, closeButtonStyle });

  const imageSources = getImages(
    images as unknown as FrontifyImageContent[] | null,
    isHorizontal
  );

  // we want to only close the modal if the link is internal
  const handleCtaClick = (url: string | undefined) => {
    if (url && isInternalLink(url)) {
      onClose();
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div
        role='dialog'
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className={cx(classes.content, {
          [classes.horizontalContent]: isHorizontal,
        })}
      >
        <ButtonBase
          className={classes.closeButton}
          disableRipple
          aria-label='close modal'
          onClick={onClose}
        >
          <Icon
            name={IconName.CLOSE}
            width={24}
            className={classes.closeIcon}
          />
        </ButtonBase>

        <div className={classes.imageContainer}>
          {imageSources && (
            <ArtDirectedImage
              images={imageSources}
              alt=''
              role='presentation'
              pictureProps={{ className: classes.image }}
            />
          )}
        </div>

        <div className={classes.contentContainer}>
          {showLogo && (
            <div>
              <TextLogo width='75' color={brandColors.primaryMain} />
            </div>
          )}

          <div className={classes.verticalGapZero}>
            {heading && (
              <Heading
                level='h2'
                classNameOverride={cx(classes.heading, {
                  [classes.headingSm]: headingSize === HeadingSize.SM,
                })}
                id={titleId}
              >
                {heading}
              </Heading>
            )}
            {subheading && (
              <RichText
                content={subheading}
                className={classes.subheading}
                textId={descriptionId}
              />
            )}
          </div>

          <div className={classes.verticalGapZero}>
            {cta && (
              <Button
                label={cta.fields.text}
                className={classes.cta}
                href={cta.fields.url}
                onClick={() => handleCtaClick(cta.fields.url)}
              />
            )}
            {secondaryCta && (
              <Button
                label={secondaryCta.fields.text}
                className={classes.secondaryCTA}
                variant={ButtonVariant.TERTIARY}
                href={secondaryCta.fields.url}
                onClick={() => handleCtaClick(secondaryCta.fields.url)}
              />
            )}
            {!secondaryCta && secondaryCtaLabel && (
              <Button
                label={secondaryCtaLabel}
                className={classes.secondaryCTA}
                variant={ButtonVariant.TERTIARY}
                onClick={onClose}
              />
            )}
          </div>

          {termsAndConditions && (
            <RichText
              content={termsAndConditions}
              className={classes.termsAndConditions}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}
